import React, { useMemo } from "react";
import { Container } from "reactstrap";
import { Stack } from "react-bootstrap";
import { PageHeader } from "../../../CommonElements/PageTitle";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Tabs } from "antd";
import AirportReceptionList from "./AirportReceptionList";
import { RealEstate01FreeIcons } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Kaaba01Icon } from "@hugeicons/core-free-icons/index";
import { permissions } from "@/utils/constants";
import usePermissions from "@/utils/hooks/usePermissions";
import { useSystem } from "@/utils/hooks/useLocation";

const ReceptionJeddah = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    hasAnyPermission
  } = usePermissions();
  const { from_id } = useSystem();

  const canListDelegation = hasAnyPermission([
    permissions.list_port_reception_cards_from_missions
  ]);
  const canListNusuk = hasAnyPermission([
    permissions.list_port_reception_cards_from_nask
  ]);

  /** First tab the user is allowed to see: prefer "من البعثة" when both apply */
  const defaultSource = useMemo(
    () =>
      canListDelegation
        ? "delegation"
        : canListNusuk
          ? "nusuk"
          : "delegation",
    [canListDelegation, canListNusuk]
  );

  const sourceParam = searchParams.get("source");
  const currentSource = sourceParam || defaultSource;

  const handleTabChange = (key) => {
    setSearchParams({ source: key });
  };

  const tabItems = useMemo(
    () =>
      [
        {
          key: "delegation",
          label: t("FROM_CAMPAIGN"),
          icon: <HugeiconsIcon size={14} icon={RealEstate01FreeIcons} />,
        },
        {
          key: "nusuk",
          label: t("FROM_NUSUK"),
          icon: <HugeiconsIcon size={14} icon={Kaaba01Icon} />,
        },
      ].filter((tab) =>
        tab.key === "delegation" ? canListDelegation : canListNusuk
      ),
    [canListDelegation, canListNusuk, t]
  );

  // Keep ?source= aligned with allowed tabs (default to first permitted tab) so deep links and AirportReceptionList match
  React.useEffect(() => {
    if (!tabItems.length) return;
    if (!sourceParam) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("source", defaultSource);
          return next;
        },
        { replace: true }
      );
      return;
    }
    if (!tabItems.some((tab) => tab.key === sourceParam)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("source", tabItems[0].key);
          return next;
        },
        { replace: true }
      );
    }
  }, [sourceParam, defaultSource, setSearchParams, tabItems]);

  return (
    <Stack gap={3}>
      <PageHeader title={t("RECEPTION_MAKKAH")} sticky={false} />
      <Container fluid>
        {tabItems.length ? (
          <>
            <Tabs
              activeKey={currentSource}
              items={tabItems}
              onChange={handleTabChange}
              className="mb-3"
            />
            <AirportReceptionList activeSource={currentSource} />
          </>
        ) : (
          <div className="text-muted">{t("NO_PERMISSIONS_AVAILABLE")}</div>
        )}
      </Container>
    </Stack>
  );
};

export default ReceptionJeddah;
